import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import type { MediaItem } from "@gooner/shared";
import { pickRenditionHeight, rungUrl } from "@gooner/shared";
import { deviceDpr } from "../../display/playbackBudget";
import { MediaSurface } from "../ui/MediaSurface";
import { IconClose } from "../ui/icons";

/** What Tab can land on inside the viewer — the trap below cycles through these. */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * The shared clip-preview shell used by BOTH the public preview (ClipEditModal) and
 * the admin clip editor (ClipLightbox). It owns everything the two used to duplicate:
 * the portaled overlay, close-on-backdrop / Escape, the centered media stage, and a
 * titled right panel. Each caller supplies the right-panel content via `children`
 * (and an optional pinned `footer`), plus an optional `mediaOverlay` layered over the
 * media (e.g. a "Banned" badge). When `onPrev`/`onNext` are given, ‹ › buttons and
 * arrow-key navigation appear (arrows are ignored while typing in an input).
 *
 * `onPlayable` fires when the media can play through. The viewer does not count views
 * itself: the public preview counts one (it is somebody watching a clip), the admin editor
 * does not (it is curation), and which is which is the caller's to say.
 *
 * Portaled to <body> so it centers on the viewport instead of being trapped inside
 * the backdrop-blurred nav header (a fixed-position containing block).
 */
export function ClipViewer({
  clip,
  title,
  onClose,
  onPrev,
  onNext,
  onPlayable,
  mediaOverlay,
  footer,
  children,
}: {
  clip: MediaItem;
  title: string;
  onClose: () => void;
  onPrev?: () => void;
  onNext?: () => void;
  onPlayable?: () => void;
  mediaOverlay?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
}) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement | null>(null);

  /*
   * A REAL MODAL, for the keyboard as well as the mouse (QA 2026-10-05). On open: focus
   * moves INTO the dialog — the panel itself, not the × (a focused × would turn Space
   * into "close") — and the page behind stops scrolling, so Space no longer scrolls the
   * Browse grid out from under the clip. On close: the scroll comes back as it was and
   * focus returns to whatever opened the viewer (the tile's button), so a keyboard user
   * carries on from the tile they were on. Mount/unmount only: Prev/Next swap `clip` on
   * this same instance, and that is not a reopen.
   */
  useEffect(() => {
    const returnTo = document.activeElement as HTMLElement | null;
    const root = document.documentElement;
    const prevOverflow = root.style.overflow;
    root.style.overflow = "hidden";
    panelRef.current?.focus({ preventScroll: true });
    return () => {
      root.style.overflow = prevOverflow;
      if (returnTo?.isConnected) returnTo.focus({ preventScroll: true });
    };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      // FOCUS TRAP: Tab cycles inside the dialog. Focus that has wandered out (a click
      // on the clip lands it on <body>) is brought back in, unless it is inside some
      // OTHER dialog stacked on top of this one (the sign-in modal), which owns it.
      if (e.key === "Tab") {
        const panel = panelRef.current;
        if (!panel) return;
        const items = [...panel.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
          (el) => el.getClientRects().length > 0,
        );
        const active = document.activeElement as HTMLElement | null;
        const inside = !!active && panel.contains(active);
        if (!inside && active?.closest('[role="dialog"]')) return;
        if (items.length === 0) {
          e.preventDefault();
          panel.focus();
          return;
        }
        const first = items[0]!;
        const last = items[items.length - 1]!;
        if (!inside || (e.shiftKey ? active === first || active === panel : active === last)) {
          e.preventDefault();
          (e.shiftKey ? last : first).focus();
        }
        return;
      }
      // Don't hijack arrows while typing in the tag / new-collection inputs.
      const el = document.activeElement;
      if (el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA")) return;
      // A modified arrow is the browser's or the OS's (Alt+Left is Back, Cmd+Left is
      // Back on macOS), not a clip step. Taking it here also preventDefault'ed Back.
      if (e.altKey || e.ctrlKey || e.metaKey) return;
      if (e.key === "ArrowRight" && onNext) {
        e.preventDefault();
        onNext();
      } else if (e.key === "ArrowLeft" && onPrev) {
        e.preventDefault();
        onPrev();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, onPrev, onNext]);

  const aspect = clip.width && clip.height ? clip.width / clip.height : 9 / 16;
  const hasNav = Boolean(onPrev || onNext);

  /*
   * Ask for the ladder rung this stage can actually show, not the master.
   *
   * The master is whatever was ingested — 4,100 of them are taller than 1080 and they
   * hold 18.8GB of the 34.6GB library — while this stage is ~520 CSS px on a desktop and
   * ~410 on a phone. Everything above that was decoded and thrown away, and it is the
   * single biggest thing this site puts on the wire: measured on prod, clip playback is
   * p90 10.9MB per open against a 3.74MB master average, and the box bursts to 170Mbit/s
   * on six concurrent viewers while its CPU sits at 16%.
   *
   * MEASURED, not derived from the breakpoint: the stage is `flex-1` under a detail panel
   * that takes its own share, so its height is emergent and the two layouts disagree
   * about it by ~100px. `useLayoutEffect` runs before paint, so the rung is known by the
   * time MediaSurface mounts and there is no first-frame fetch of a master to abandon.
   *
   * At `lg` that measurement is exact — the stage is `58dvh`, which is settled at first
   * paint. Below it the stage is whatever the detail panel leaves, and the panel grows
   * when its tags and stats arrive: measured 476px at first paint settling to 407px, so a
   * phone can land one rung high (1080 where 720 would do). Left that way on purpose.
   * Re-measuring later would mean swapping `src` mid-play, which restarts playback and
   * re-downloads; and of the two ways to be wrong on the surface where someone is
   * deliberately watching, too much detail is the right one. It still cuts the clip in
   * half — 3.78MB master to a 1.83MB 1080 rung, measured.
   *
   * DPR is capped at 2 (the admin's phone cap on a phone — deviceDpr, shared with the wall)
   * deliberately: a 3x phone would otherwise ask for a rung by screen
   * density rather than by how big the video actually looks, over cellular.
   *
   * A cold rung is not a failure: the server redirects to the master and warms the rung
   * behind it (see media.ts), so the worst case is today's behaviour for one play.
   */
  const stageRef = useRef<HTMLDivElement>(null);
  const [rung, setRung] = useState<number | null | undefined>(undefined);
  useLayoutEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const css = el.getBoundingClientRect().height;
    // The wall's helper (#13): 2 at most, the admin's phone cap on a phone. No ceiling
    // and no decode budget here — one clip, full screen, is not the wall's problem.
    const dpr = deviceDpr().effective;
    setRung(css > 0 ? pickRenditionHeight(Math.round(css * dpr)) : null);
  }, [clip.url]);

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
      onMouseDown={onClose}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onMouseDown={(e) => e.stopPropagation()}
        className="flex max-h-[92dvh] outline-none w-full max-w-4xl flex-col overflow-hidden rounded-2xl border border-[var(--color-edge)] bg-[var(--color-panel)] shadow-2xl lg:flex-row"
      >
        {/* left: media stage (with optional prev/next). min-w-0 lets this flex cell
            shrink below the media's intrinsic width so a tall portrait clip can't blow
            the modal past its max width and clip the right panel under overflow-hidden. */}
        <div className="flex min-h-0 min-w-0 flex-1 items-center justify-center p-4">
          {/* `self-stretch` below `lg` is what gives the media box a parent with a
              DEFINITE height to size against: `items-center` above makes this item
              shrink-to-fit, and a percentage height against an indefinite parent
              resolves to nothing at all. At `lg` the stage has room to spare and the
              centring is what looks right. */}
          <div className="relative flex min-h-0 w-full min-w-0 items-center justify-center self-stretch lg:self-center">
            {hasNav ? (
              <button
                type="button"
                aria-label="Previous clip"
                disabled={!onPrev}
                onClick={onPrev}
                className="absolute left-0 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-black/50 text-lg text-white transition hover:bg-black/70 disabled:opacity-25 pointer-coarse:h-11 pointer-coarse:w-11"
              >
                ‹
              </button>
            ) : null}
            {/* Below `lg` the card is a COLUMN: the detail panel takes its content
                height and the media gets what is left, so the media's height has to come
                from the STAGE and not the viewport. It was a flat `58vh` — 490px inside a
                398px stage on a 390x844 phone, which laid 45px of clip over the title and
                the close button, and in landscape covered most of the panel. From `lg` the
                card is a ROW, the stage has no height of its own to inherit, and the
                viewport sizing is both correct and what the modal was drawn to. */}
            <div
              ref={stageRef}
              className="relative h-full max-h-full select-none overflow-hidden rounded-lg bg-black lg:h-[58dvh] lg:max-h-[72dvh]"
              style={{ aspectRatio: String(aspect), maxWidth: "100%" }}
            >
              {/* Held back one commit — `rung` is undefined only until the layout effect
                  above has measured, which happens before paint. Mounting the surface
                  first would start the master downloading and then swap the src, which
                  costs the bytes this whole change exists to save. The poster carries the
                  frame in the meantime, exactly as it does while the video buffers. */}
              {rung === undefined ? (
                clip.posterUrl ? (
                  <img src={clip.posterUrl} alt="" className="h-full w-full object-contain" />
                ) : null
              ) : (
                <MediaSurface
                  kind={clip.kind}
                  src={rungUrl(clip.url, rung)}
                  poster={clip.posterUrl}
                  fit="contain"
                  loop
                  onCanPlayThrough={onPlayable}
                  className="pointer-events-none"
                />
              )}
              {mediaOverlay}
            </div>
            {hasNav ? (
              <button
                type="button"
                aria-label="Next clip"
                disabled={!onNext}
                onClick={onNext}
                className="absolute right-0 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-black/50 text-lg text-white transition hover:bg-black/70 disabled:opacity-25 pointer-coarse:h-11 pointer-coarse:w-11"
              >
                ›
              </button>
            ) : null}
          </div>
        </div>

        {/* right: titled panel with caller-supplied content */}
        {/* `shrink-0` is a ROW instruction — it stops this 320px column being squeezed
            by a wide clip — and applying it in the stacked layout too meant the panel
            took its full content height out of a short viewport and left the media stage
            with nothing. On a landscape phone that was literally zero. Below `lg` it
            shares the height instead, capped so the clip always keeps most of it, and the
            inner `overflow-y-auto` below does the rest. */}
        <div className="flex max-h-[60%] w-full min-h-0 flex-col border-t border-[var(--color-edge)] lg:max-h-none lg:w-80 lg:shrink-0 lg:border-l lg:border-t-0">
          <div className="flex items-center justify-between border-b border-[var(--color-edge)] p-3">
            <h3 id={titleId} className="text-sm font-semibold text-zinc-100">{title}</h3>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="rounded p-1 text-zinc-400 hover:bg-[var(--color-panel-2)] hover:text-white"
            >
              <IconClose />
            </button>
          </div>
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-3">{children}</div>
          {footer}
        </div>
      </div>
    </div>,
    document.body,
  );
}
